import type { EditCommand } from './commands';
import { type ClipItem, type ClipFit, type Edl, DEFAULT_TRACK_IDS } from './edl';
import { apply } from './reducer';

/**
 * 가로 화면을 세로로 어떻게 담을지 — **사람이 고른다.**
 *
 * 🔴 왜 필요한가. 16:9 를 9:16 으로 자르면 **가로의 68.4% 가 사라진다**(남는 것은 31.6%).
 * 그런데 지금까지 그 잘리는 자리는 언제나 «한가운데»로 못 박혀 있었다. 실물로 확인한 사례:
 * 1인칭 게임 클립에서 왼쪽 UI·오른쪽 채팅과 **웹캠(얼굴)이 통째로 잘려 나갔다.**
 *
 * 실사용자가 이 문제를 «얼굴이 잘린다»는 형태로는 지적했지만(그래서 얼굴 PIP 이 생겼다),
 * «가운데가 항상 옳은 자리인가»는 아무도 묻지 않았다. 검수에 쓴 클립이 3인칭이라
 * 주인공이 마침 가운데 있었기 때문이다 — **표본이 결함을 가렸다.**
 *
 * 자동 판정(saliency)은 하지 않는다. 예전에 시험했을 때 AI 비용 대비 결과가 만족스럽지
 * 않았다는 것이 소유자 판단이다. 대신 **두 가지 중에 사람이 고르게 한다.**
 */

/**
 * 담는 방식.
 *  - `fill` : 세로를 꽉 채운다(좌우가 잘린다). 어디를 남길지는 {@link FramingOptions.x} 로 정한다.
 *  - `fit`  : 원본을 통째로 보여준다(위아래 검은 띠). 잘리는 것이 없다.
 */
export const FRAMING_MODES = ['fill', 'fit'] as const;
export type FramingMode = (typeof FRAMING_MODES)[number];

/** 캔버스 비율(9:16). */
const CANVAS_ASPECT = 9 / 16;

/** 소스 가로 대비, 세로 화면에 남길 수 있는 최대 폭 비율. 16:9 면 0.3164. */
export function keepWidth(sourceAspect: number): number {
  return Math.min(1, CANVAS_ASPECT / sourceAspect);
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

export interface FramingOptions {
  mode: FramingMode;
  /**
   * `fill` 에서 **남길 구간의 중심**(0..1, 소스 가로 기준). 0.5 가 지금까지의 동작이다.
   * `fit` 에서는 무시한다.
   */
  x?: number;
  /** 소스 가로세로비. 기본 16:9 — 방송 소스는 사실상 전부 이 값이다. */
  sourceAspect?: number;
}

export interface FramingPlan {
  edl: Edl;
  commands: EditCommand[];
  /** 손댄 클립 수. 0 이면 바뀐 것이 없다. */
  applied: number;
}

/** 이 크롭이 «줌»인가 — 세로 화면 폭({@link keepWidth})보다 넓으면 프레이밍용이 아니다. */
function isZoomCrop(crop: ClipItem['crop'], kw: number): boolean {
  return crop !== undefined && crop.w > kw + 0.01;
}

/**
 * 프레이밍을 적용한다.
 *
 * 🔴 **펀치 줌을 뭉개지 않는다.** 줌은 이미 크롭을 쓰고 있고(더 좁게), 그 위에 프레이밍
 * 크롭을 덮어쓰면 줌이 사라진다. 그래서 줌이 걸린 조각은 **폭을 그대로 두고 x 만 같은
 * 방향으로 옮긴다.** 그러면 «화면을 왼쪽으로 당겨 놨는데 줌 구간만 가운데로 튀는» 현상이
 * 안 생긴다.
 */
export function planFraming(edl: Edl, opts: FramingOptions): FramingPlan {
  const sourceAspect = opts.sourceAspect ?? 16 / 9;
  const kw = keepWidth(sourceAspect);
  const track = edl.tracks.find((t) => t.id === DEFAULT_TRACK_IDS.video);
  const clips = (track?.items ?? []).filter((i): i is ClipItem => i.type === 'clip');
  if (clips.length === 0) return { edl, commands: [], applied: 0 };

  const commands: EditCommand[] = [];
  const fit: ClipFit = opts.mode === 'fit' ? 'contain' : 'cover';
  const x = clamp(opts.x ?? 0.5, 0, 1);

  for (const clip of clips) {
    if (clip.fit !== fit) commands.push({ op: 'setFit', itemId: clip.id, fit });

    if (opts.mode === 'fit') {
      /*
       * 전체 보이기에서는 **프레이밍 크롭을 걷어낸다.** 남겨 두면 «전부 보여준다»면서
       * 일부를 잘라 놓은 상태가 되어 이름과 동작이 어긋난다.
       * 줌 크롭은 건드리지 않는다 — 그건 프레이밍이 아니라 연출이다.
       */
      if (clip.crop && !isZoomCrop(clip.crop, kw)) {
        commands.push({ op: 'setCrop', itemId: clip.id, crop: { x: 0, y: 0, w: 1, h: 1 } });
      }
      continue;
    }

    if (isZoomCrop(clip.crop, kw)) {
      // 줌: 폭은 그대로, 중심만 같이 옮긴다.
      const w = clip.crop!.w;
      const nx = Number(clamp(x - w / 2, 0, 1 - w).toFixed(4));
      if (Math.abs(nx - clip.crop!.x) > 0.0005) {
        commands.push({ op: 'setCrop', itemId: clip.id, crop: { ...clip.crop!, x: nx } });
      }
      continue;
    }

    // 일반 컷: 세로 화면이 실제로 쓰는 폭만큼을 x 중심으로 잘라 둔다.
    const nx = Number(clamp(x - kw / 2, 0, 1 - kw).toFixed(4));
    const next = { x: nx, y: 0, w: Number(kw.toFixed(4)), h: 1 };
    const cur = clip.crop;
    const same =
      cur &&
      Math.abs(cur.x - next.x) < 0.0005 &&
      Math.abs(cur.y - next.y) < 0.0005 &&
      Math.abs(cur.w - next.w) < 0.0005 &&
      Math.abs(cur.h - next.h) < 0.0005;
    if (!same) commands.push({ op: 'setCrop', itemId: clip.id, crop: next });
  }

  if (commands.length === 0) return { edl, commands: [], applied: 0 };

  let working = edl;
  const kept: EditCommand[] = [];
  for (const cmd of commands) {
    try {
      working = apply(working, cmd);
      kept.push(cmd);
    } catch {
      // 한 컷이 거부돼도 나머지는 적용한다 — 프레이밍은 컷마다 독립이다.
    }
  }
  return { edl: working, commands: kept, applied: kept.length };
}

/**
 * 지금 EDL 이 어떤 프레이밍인지 되읽는다. UI 가 «지금 어느 쪽이 켜져 있나»를 표시하려면
 * 필요하다 — 저장된 설정이 아니라 **결과**를 봐야 화면과 결과물이 갈리지 않는다.
 */
export function readFraming(edl: Edl, sourceAspect = 16 / 9): { mode: FramingMode; x: number } {
  const kw = keepWidth(sourceAspect);
  const track = edl.tracks.find((t) => t.id === DEFAULT_TRACK_IDS.video);
  const clips = (track?.items ?? []).filter((i): i is ClipItem => i.type === 'clip');
  if (clips.length === 0) return { mode: 'fill', x: 0.5 };
  // 한 컷이라도 `contain` 이면 전체 보이기로 본다 — 섞여 있으면 «전부 안 보이는» 쪽이 없다.
  if (clips.some((c) => c.fit === 'contain')) return { mode: 'fit', x: 0.5 };
  const framed = clips.find((c) => c.crop && !isZoomCrop(c.crop, kw));
  if (!framed?.crop) return { mode: 'fill', x: 0.5 };
  return { mode: 'fill', x: Number((framed.crop.x + framed.crop.w / 2).toFixed(4)) };
}
